import { reactive } from 'vue'
import { DEFAULT_TOOL_PREFS, type ToolPrefs } from './toolSettings'

// How the tools write dates and times (Settings > Tools). Kept here rather
// than read from the settings store so the label helpers stay testable; the
// app copies the writer's choice in at start (see services/theme/toolColors). It is
// reactive, so labels computed from it follow a change straight away.

export const TOOL_FORMAT_KEYS = [
  'clock', 'dateOrder', 'dateMonths', 'dateSeparator', 'datePad', 'dateYear',
  'relativeDates', 'ampm', 'hourPad', 'trimWholeHours',
] as const

export type ToolFormat = Pick<ToolPrefs, typeof TOOL_FORMAT_KEYS[number]>

export const toolFormat = reactive<ToolFormat>(defaultToolFormat())

export function defaultToolFormat(): ToolFormat {
  const format = {} as Record<string, unknown>
  for (const key of TOOL_FORMAT_KEYS) format[key] = DEFAULT_TOOL_PREFS[key]
  return format as ToolFormat
}

/** The English that writes month and weekday names in each order. Year first borrows the US names. */
const WORD_LOCALES: Record<ToolFormat['dateOrder'], string | undefined> = {
  auto: undefined,
  mdy: 'en-US',
  dmy: 'en-GB',
  ymd: 'en-US',
}

/** true or false for a 12- or 24-hour clock; undefined follows the system. */
export function hour12(): boolean | undefined {
  return toolFormat.clock === 'auto' ? undefined : toolFormat.clock === '12'
}

/** Whether times read on a 12-hour clock: the writer's choice, or the system's. */
export function uses12Hour(): boolean {
  return hour12() ?? new Intl.DateTimeFormat(undefined, { hour: 'numeric' }).resolvedOptions().hour12 ?? false
}

const pad = (n: number) => String(n).padStart(2, '0')

/** A date all in numbers, in the writer's order and shape: 10/31/2026, 31.10.26, 2026-10-31. */
function numberDate(date: Date, withYear: boolean): string {
  const f = toolFormat
  if (f.dateOrder === 'auto') {
    return date.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}) })
  }
  const day = f.datePad ? pad(date.getDate()) : String(date.getDate())
  const month = f.datePad ? pad(date.getMonth() + 1) : String(date.getMonth() + 1)
  const year = f.dateYear === 'short' ? pad(date.getFullYear() % 100) : String(date.getFullYear())
  const pieces = f.dateOrder === 'mdy' ? [month, day] : f.dateOrder === 'dmy' ? [day, month] : [month, day]
  if (f.dateOrder === 'ymd') pieces.unshift(year)
  else if (withYear) pieces.push(year)
  return pieces.join(f.dateSeparator)
}

/** Year first with words: "Friday, 2026 October 9", or "Oct 9" without a year. */
function yearFirstWords(date: Date, options: Intl.DateTimeFormatOptions): string {
  const parts = new Intl.DateTimeFormat('en-US', { ...options, year: undefined }).formatToParts(date)
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value
  const monthDay = [options.year ? String(date.getFullYear()) : '', part('month'), part('day')].filter(Boolean).join(' ')
  const weekday = part('weekday')
  return weekday ? `${weekday}, ${monthDay}` : monthDay
}

/**
 * A date in the writer's style, given the parts wanted. Without a day (a month
 * heading, a weekday) the names are written as they are. With months as
 * numbers, any weekday asked for comes first; year first always has its year.
 */
export function formatDate(date: Date, options: Intl.DateTimeFormatOptions): string {
  const f = toolFormat
  const locale = WORD_LOCALES[f.dateOrder]
  if (!options.day) return date.toLocaleDateString(locale, options)
  if (f.dateMonths === 'numbers') {
    const numbers = numberDate(date, !!options.year || f.dateOrder === 'ymd')
    return options.weekday ? `${date.toLocaleDateString(locale, { weekday: options.weekday })} ${numbers}` : numbers
  }
  if (f.dateOrder === 'ymd') return yearFirstWords(date, options)
  return date.toLocaleDateString(locale, options)
}

/** A full date all in numbers, whatever the months setting: for backup dates and the like. */
export function formatNumberDate(date: Date): string {
  return numberDate(date, true)
}

/** A full date in numbers with its time: "10/31/2026, 3:05 PM". */
export function formatNumberDateTime(date: Date): string {
  return `${formatNumberDate(date)}, ${formatTime(date)}`
}

const AM_PM: Record<Exclude<ToolFormat['ampm'], 'auto'>, [string, string]> = {
  AM: ['AM', 'PM'],
  am: ['am', 'pm'],
  'a.m.': ['a.m.', 'p.m.'],
}

/** Times follow the date order's English; year first (ISO) leaves them to the system. */
function timeLocale(): string | undefined {
  return toolFormat.dateOrder === 'ymd' ? undefined : WORD_LOCALES[toolFormat.dateOrder]
}

/**
 * A time of day on the writer's clock, like 9:30 a.m., 3 PM or 09:30.
 * `wholeHour` drops ":00" on a 12-hour clock whatever the setting, as for the hour labels down a day.
 */
export function formatTime(date: Date, wholeHour = false): string {
  const f = toolFormat
  const twelve = uses12Hour()
  const parts = new Intl.DateTimeFormat(timeLocale(), { hour: 'numeric', minute: '2-digit', hour12: twelve }).formatToParts(date)
  const trim = twelve && (f.trimWholeHours || wholeHour) && date.getMinutes() === 0
  const minuteAt = parts.findIndex((p) => p.type === 'minute')
  return parts.map((p, i) => {
    if (trim && (i === minuteAt || (i === minuteAt - 1 && p.type === 'literal'))) return ''
    if (p.type === 'hour' && !twelve && f.hourPad !== 'auto') return f.hourPad === 'pad' ? pad(date.getHours()) : String(date.getHours())
    if (p.type === 'dayPeriod' && f.ampm !== 'auto') return AM_PM[f.ampm][date.getHours() < 12 ? 0 : 1]
    return p.value
  }).join('')
}

const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString()

/** A time span, as an event's: "9:00 – 10:30 AM" keeps one AM when both share it. */
export function formatTimeRange(start: Date, end: Date): string {
  const from = formatTime(start)
  const to = formatTime(end)
  const morning = start.getHours() < 12
  if (uses12Hour() && morning === end.getHours() < 12) {
    const period = amPmWords()[morning ? 0 : 1]
    if (from.endsWith(period) && to.endsWith(period)) return `${from.slice(0, -period.length).trimEnd()} – ${to}`
  }
  return `${from} – ${to}`
}

/**
 * The days from `start` to `end` (both shown), as a heading: "Nov 2 – 8, 2026",
 * "2 – 8 Nov 2026", "2026 Nov 2 – 8", or two dates in numbers.
 */
export function formatDayRange(start: Date, end: Date): string {
  const f = toolFormat
  const full = { month: 'short', day: 'numeric', year: 'numeric' } as const
  if (sameDay(start, end)) return formatDate(start, { ...full, month: 'long' })
  if (f.dateMonths === 'numbers') return `${formatNumberDate(start)} – ${formatNumberDate(end)}`
  if (f.dateOrder !== 'ymd') {
    // formatRange is in every engine the app runs on, though not in the TypeScript lib it targets.
    const format = new Intl.DateTimeFormat(WORD_LOCALES[f.dateOrder], full) as Intl.DateTimeFormat & { formatRange(a: Date, b: Date): string }
    return format.formatRange(start, end).replace(/\s*–\s*/, ' – ')
  }
  const month = (date: Date) => date.toLocaleDateString('en-US', { month: 'short' })
  const sameYear = start.getFullYear() === end.getFullYear()
  const to = sameYear && start.getMonth() === end.getMonth()
    ? String(end.getDate())
    : `${sameYear ? '' : `${end.getFullYear()} `}${month(end)} ${end.getDate()}`
  return `${start.getFullYear()} ${month(start)} ${start.getDate()} – ${to}`
}

/** A date with a time, as in a sprint's history row. */
export function formatDateTime(date: Date, options: Intl.DateTimeFormatOptions): string {
  return `${formatDate(date, options)}, ${formatTime(date)}`
}

/** A stored time of day ('HH:MM') on the writer's clock. */
export function clockLabel(time: string): string {
  const [hours, minutes] = time.split(':').map(Number)
  return formatTime(new Date(2000, 0, 1, hours, minutes))
}

/** How the writer's clock writes AM and PM, for pickers: ['AM', 'PM'] and the like. */
export function amPmWords(): [string, string] {
  if (toolFormat.ampm !== 'auto') return AM_PM[toolFormat.ampm]
  const format = new Intl.DateTimeFormat(timeLocale(), { hour: 'numeric', hour12: true })
  const period = (hour: number, fallback: string) =>
    format.formatToParts(new Date(2000, 0, 1, hour)).find((p) => p.type === 'dayPeriod')?.value ?? fallback
  return [period(9, 'AM'), period(21, 'PM')]
}
